import { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Grid,
  GridColumn,
  Modal,
  Spinner,
  TextField,
  useSnackbar,
} from "@walmart-web/livingdesign-components";
import moment from "moment";
import {
  APPROVING_ATTORNEY,
  CURRENT_WALMART_RATE,
  END_DATE,
  JUSTIFICATION_FOR_RATE,
  MATTER_NUMBER,
  MIN_CALENDAR_DAYS,
  MSR_CURRENT_STANDARD_RATE,
  MSR_UPDATE_SUCCESS_MSG,
  PRACTICE_AREA,
  PROPOSED_WALMART_RATE,
  REQUESTER_EMAIL,
  START_DATE,
  UPDATE_PRACTICE_AREA_SUCCESS_MSG,
} from "../../../constants/constants";
import {
  BACKEND_DATE_FORMAT,
  DATE_END_TIME,
  DATE_START_TIME,
} from "../../../constants/dateFormats";
import Label from "../../../components/TextField/Label/Label";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import EditRateModalStyles from "./EditRateModal.styles";
import { getErrorMessage, renderTernary } from "../../../helpers/utils";
import {
  START_END_DATE_VALIDATION_MESSAGE,
  INVALID_DATE_RANGE_MESSAGE,
  INVALID_DATE
} from "../../../constants/messages";
import MuiTextField from "@mui/material/TextField";
import { CANCEL, EDIT } from "../../../constants/actionButtons";
import { useForm } from "react-hook-form";
import { filterPayload } from "../../../helpers/commonUtils";
import { EXTERNAL_USER_SERVICE } from "../../../constants/baseURLs";
import { convertToUTCDateTime } from "../../../helpers/utcUtils";
import { patchService, postService } from "../../../axios/axios";
import { Autocomplete } from "@mui/material";
import { useSelector } from "react-redux";

/**
 * This component is used to only edit matter specific rate.
 */
const EditRateModal = (props) => {
  const classes = EditRateModalStyles();
  const { open, setOpen, title, message, fromMatterSpecificRates, editRateValues, getRates } = props
  const [editRateLoading, setEditRateLoading] = useState(false);
  const [editedValues, setEditedValues] = useState(editRateValues);
  const { formState: { errors},setError, clearErrors } = useForm({ mode: "all" });
  const { addSnack } = useSnackbar();
  const practiceAreas = useSelector((state) => state?.matter?.practiceAreas);
  
  useEffect(()=>{
    setEditedValues(editRateValues);
  }, [open])

  const handleClose = () => { 
    setOpen(false);
  }

  const handleInputChange = (name, value) => {
    if (name === "startDate" || name === "endDate") {
      if ((name === "startDate" && new Date(editRateValues?.["endDate"]) < new Date(value))) {
        setError('endDate', { type: 'custom', message: START_END_DATE_VALIDATION_MESSAGE });
      } else if ((name === "endDate" && new Date(value) < new Date(editRateValues?.["startDate"]))) {
        setError('endDate', { type: 'custom', message: START_END_DATE_VALIDATION_MESSAGE });
      } else {
        clearErrors('endDate');
      }
    }
    if ((name === "proposedWalmartRate" || name === "currentStandardRate") && (value === "0" || value.includes('-'))){
      value = '' 
    }
    setEditedValues((prev)=>({...prev, [name]: value}))
  }

  /**
   * Function to handle date picker error
   * @param {*} err
   * @param {*} dateType
   */
  const handleDateError = (err, dateType) => {
    if(err) {
      setError(dateType, { type: 'custom', message: INVALID_DATE_RANGE_MESSAGE })
    } else {
      clearErrors(dateType)
    }
  };

  const onEditRate = () => {
    setEditRateLoading(true);
    const body = {
        id : editedValues?.["id"],
        startDate : convertToUTCDateTime({date: editedValues?.["startDate"], time: DATE_START_TIME}),
        endDate : (editedValues?.["endDate"] && editedValues?.["endDate"] !== INVALID_DATE) ? convertToUTCDateTime({date: editedValues?.["endDate"], time: DATE_END_TIME}) : null,
        currentWalmartRate : parseFloat(editedValues?.["currentWalmartRate"],2),
        currentStandardRate : parseFloat(editedValues?.["currentStandardRate"],2),
        proposedWalmartRate : parseFloat(editedValues?.["proposedWalmartRate"],2),
        ...(!fromMatterSpecificRates && { practiceAreaCode : editedValues?.["practiceAreaCode"] })
    }
    const filteredBody=filterPayload(body)
    fromMatterSpecificRates ? handleEditMatterSpecificRate(filteredBody) : handleEditPracticeAreaSpecificRate(filteredBody);

  };

  const handleEditMatterSpecificRate = (filteredBody) => {
    postService(EXTERNAL_USER_SERVICE, `/matter-specific-rates/v1/${editedValues?.["id"]}`, filteredBody)
    .then((response) => {
      addSnack({message: MSR_UPDATE_SUCCESS_MSG });
      getRates();
    })
    .catch((error) => {
      addSnack({ message: getErrorMessage(error)});
    })
    .finally(() => {
      setEditRateLoading(false);
      setOpen(false);
    });
  }

  const handleEditPracticeAreaSpecificRate = (filteredBody) => {
    patchService(EXTERNAL_USER_SERVICE, `/practice-area-specific-rates/v1/${editedValues?.["id"]}`, filteredBody)
    .then((response) => {
      addSnack({message: UPDATE_PRACTICE_AREA_SUCCESS_MSG });
      getRates();
    })
    .catch((error) => {
      addSnack({ message: getErrorMessage(error)});
    })
    .finally(() => {
      setEditRateLoading(false);
      setOpen(false);
    });
    
  }

  const checkMandatoryandDirty =()=>{
    return ((editedValues?.["startDate"] !== "Invalid date"  && editedValues?.["currentStandardRate"] !== ""
    && editedValues?.["proposedWalmartRate"] !== "") && (editRateValues?.["startDate"] !== editedValues?.["startDate"]
    || editRateValues?.["endDate"] !== editedValues?.["endDate"]
    || editRateValues?.["currentWalmartRate"] !== editedValues?.["currentWalmartRate"] ||
    editRateValues?.["currentStandardRate"] !== editedValues?.["currentStandardRate"] ||
    editRateValues?.["proposedWalmartRate"] !== editedValues?.["proposedWalmartRate"])) || (editedValues?.["practiceAreaCode"] !== undefined &&
		 editRateValues?.["practiceAreaCode"] !== editedValues?.["practiceAreaCode"])
  }

  const EditRateActions = (props) => {
    const { onEditRate, onCancel, editRateLoading } = props;
    return (
      <ButtonGroup>
          <Button variant="secondary"
          data-testid={`cancel-button`}
          onClick={onCancel}>
            {CANCEL}
          </Button>
          <Button
            variant="primary"
            onClick={onEditRate}
            data-testid={`save-button`}
            id={`save-button`}
            disabled={(Object.keys(errors).length > 0? true : !checkMandatoryandDirty()) || editRateLoading }
          >
            {EDIT} {editRateLoading && <Spinner color="white" size="small" />}
          </Button>
        </ButtonGroup>
    );
  };

  return (
    <Modal
      data-testid="edit-rate-modal"
      actions={
        <EditRateActions
          onEditRate={onEditRate}
          onCancel={handleClose}
          editRateLoading={editRateLoading}
          />
      }
      isOpen={open}
      onClose={handleClose}
      children={message}
      size="large"
      title={title}
    >
      <Grid className={classes.accordionDetailsGrid} key={editRateValues?.matterNumber}>
        <GridColumn sm="7" md="7" lg="7">
          {fromMatterSpecificRates ?
          <>
          <Label text={MATTER_NUMBER} />
          <TextField
            className={classes.editTextfield}
            value={editedValues?.["matterNumber"] ?? ""}
            textFieldProps={{
              name: "matterNumber",
              id: "matterNumber",
            }}
            type="text"
            data-testid="handleInputChange-matterNumber"
            disabled
          />
          </> : 
          <>
          <Label text={PRACTICE_AREA} mandatoryField />
          <Autocomplete
              sx={{ width: "80%" }}
              multiple={false}
              disablePortal
              options={practiceAreas?.map((option) => option.name)}
              value={practiceAreas?.find((option) => option.code === editRateValues?.["practiceAreaCode"])?.name}
              renderInput={(params) => (
                <MuiTextField {...params} label="" placeholder="Select practice area" />
              )}
              isOptionEqualToValue={(option, value) => option === value || !value}
              onChange={(event, newValue) => {
                if (!newValue) return;
                const selectedOption = practiceAreas?.find((option) => option.name === newValue);
                if (selectedOption) {
                  handleInputChange("practiceAreaCode", selectedOption.code);
                }
              }}
              id="editpracticearearate-practice-area"
              data-testid="editpracticearearate-practice-area"
            />
          </>}
          <div className={classes.contentSpacing16}>
            <Label text={START_DATE} mandatoryField />
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                sx={{ width: "80%" }}
                required={true}
                onError={(err) => {
                  handleDateError(err, 'startDate')
                }}
                value={renderTernary(editedValues?.["startDate"], moment.utc(editedValues?.["startDate"]).local(), null)}
                onChange={(e) => handleInputChange("startDate", moment(e).format(BACKEND_DATE_FORMAT))}
                slotProps={{ popper: { disablePortal:true}, inputAdornment: { position: "end" }, textField: { InputProps: { style: { color: '#2E2F32' } }, name: "startDate", "data-testid": "handleInputChange-startDate","id": "startDate", helperText: errors?.startDate?.message } }}
                minDate={moment.utc(editedValues?.["createdTs"]).local().subtract(MIN_CALENDAR_DAYS, 'days')}
              />
            </LocalizationProvider>
          </div>
          <div className={classes.contentSpacing16}>
            <Label text={END_DATE}/>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                sx={{ width: "80%" }}
                onError={(err) => {
                  handleDateError(err, 'endDate')
                }}
                value={renderTernary(editedValues?.["endDate"], moment.utc(editedValues?.["endDate"]).local(), null)}
                onChange={(e) => handleInputChange("endDate", moment(e).format(BACKEND_DATE_FORMAT))}
                slotProps={{ 
                  popper: { disablePortal:true},
                  inputAdornment: { position: "end" }, 
                  textField: { InputProps: { style: { color: '#2E2F32' } }, 
                  name: "endDate", 
                  "data-testid": "handleInputChange-endDate",
                  "id": "endDate", helperText: errors?.endDate?.message } }}
                minDate={moment.utc(editedValues?.["startDate"]).local()}
              />
            </LocalizationProvider>
          </div>
          <div className={classes.contentSpacing16}>
            <Label text={CURRENT_WALMART_RATE} />
            <TextField
              className={classes.editTextfield}
              textFieldProps={{
                name: "currentWalmartRate",
                id: "currentWalmartRate",
                min: 1
              }}
              value={editedValues?.["currentWalmartRate"] ?? ""}
              onChange={(e) => handleInputChange(e.target.name, e.target.value)}
              type="number"
              data-testid="handleInputChange-currentWalmartRate"
            />
          </div>
          <div className={classes.contentSpacing16}>
            <Label text={MSR_CURRENT_STANDARD_RATE} mandatoryField />
            <MuiTextField
              onChange={(e) =>
                handleInputChange("currentStandardRate", e.target.value)
              }
              className={classes.editTextfield}
              value={editedValues?.["currentStandardRate"]}
              textFieldProps={{
                name: "currentStandardRate",
                id:"edittimekeeperprofile-currentStandardRate",
              }}
              type="number"
              data-testid="handleInputChange-currentStandardRate"
              InputProps={{
                inputProps: {
                  min: 1,
                }
              }}
            />
          </div>
          <div className={classes.contentSpacing16}>
            <Label text={PROPOSED_WALMART_RATE} mandatoryField />
            <MuiTextField
              onChange={(e) =>
                handleInputChange("proposedWalmartRate", e.target.value)
              }
              className={classes.editTextfield}
              value={editedValues?.["proposedWalmartRate"]}
              textFieldProps={{
                name: "proposedWalmartRate",
                id:"edittimekeeperprofile-proposedWalmartRate",
              }}
              type="number"
              data-testid="handleInputChange-proposedWalmartRate"
              InputProps={{
                inputProps: {
                  min: 1,
                }
              }}
            />
          </div>
        </GridColumn>
        <GridColumn sm="5" md="5" lg="5">
          <Label text={APPROVING_ATTORNEY} />
          <TextField
            className={classes.editTextfield}
            value={editedValues?.["walmartApprovingAttorneyFullName"] ?? ""}
            textFieldProps={{
              name: "approvedAttorney",
              id: "approvedAttorney"
            }}
            type="text"
            data-testid="handleInputChange-approvedAttorney"
            disabled
          />
          <div className={classes.contentSpacing16}>
            <Label text={REQUESTER_EMAIL} />
            <TextField
              className={classes.editTextfield}
              value={editedValues?.["requesterEmailId"] ?? ""}
              textFieldProps={{
                name: "requesterEmail",
                id: "requesterEmail"
              }}
              type="text"
              data-testid="handleInputChange-requesterEmail"
              disabled
            />
          </div>
          <div className={classes.contentSpacing16}>
            <Label text={JUSTIFICATION_FOR_RATE} />
            <TextField
              className={classes.editTextfield}
              value={editedValues?.["outsideOrgJustification"] ?? ""}
              textFieldProps={{
                name: "justificationForRate",
                id: "justificationForRate"
              }}
              type="text"
              data-testid="handleInputChange-justificationForRate"
              disabled
            />
          </div>
        </GridColumn>
      </Grid>
  </Modal>  
);
};

export default EditRateModal;
